import {FC, HTMLAttributes} from 'react';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import Button from 'components/Button';
import Chain from 'components/Layout/Chain';
import {button, buttonActive, ellipse} from './style.module.css';
import paginationButtonProps from './utils';

const PAGE_START = 1;

type TablePaginationProps = Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> & {
    hideForSinglePage?: boolean;
    isAmbigousStop?: boolean;
    onChange: (page: number) => void;
    onFirst: () => void;
    onLast: () => void;
    pageCurrent: number;
    pageStop: number;
    paginationPadding?: number;
};

const TablePagination: FC<TablePaginationProps> = ({
    className,
    hideForSinglePage,
    isAmbigousStop,
    onChange,
    onFirst,
    onLast,
    pageCurrent,
    pageStop,
    paginationPadding = 9,
    ...props
}) => {
    const {t} = useTranslation('Table.TableComponents.TablePagination');

    if (hideForSinglePage && pageStop <= 1) return null;

    const pageButtons = paginationButtonProps({
        current: pageCurrent,
        max: pageStop,
        min: PAGE_START,
        padding: paginationPadding,
    });

    return (
        <Chain className={clsx('bg-white w-fit rounded', className)} {...props}>
            <Button
                aria-label="Previous page"
                className={button}
                disabled={pageCurrent === PAGE_START}
                icon="small_arrow_left"
                kind="secondary"
                onClick={() => onChange(pageCurrent - 1)}
                size="sm"
            />
            {pageButtons.map(
                (pageProps) =>
                    (pageProps.kind === 'ellipsis' && (
                        <div key={pageProps.key} className={ellipse}>
                            ...
                        </div>
                    )) ||
                    (pageProps.kind === 'button' && (
                        <Button
                            key={pageProps.key}
                            aria-label={`Page ${pageProps.value}`}
                            className={clsx(
                                button,
                                pageCurrent === pageProps.value && buttonActive,
                            )}
                            kind={
                                pageCurrent === pageProps.value
                                    ? 'primary'
                                    : 'secondary'
                            }
                            onClick={
                                pageProps.isFirst
                                    ? onFirst
                                    : pageProps.isLast
                                      ? onLast
                                      : pageCurrent === pageProps.value
                                        ? undefined
                                        : () => onChange(pageProps.value)
                            }
                            size="sm"
                        >
                            {pageProps.isLast && isAmbigousStop
                                ? t('last')
                                : pageProps.label}
                        </Button>
                    )),
            )}
            <Button
                aria-label="Next page"
                className={button}
                disabled={pageCurrent === pageStop}
                icon="small_arrow_right"
                kind="secondary"
                onClick={() => onChange(pageCurrent + 1)}
                size="sm"
            />
        </Chain>
    );
};

export default TablePagination;
